import { useEffect, useRef, useState } from "react";
import { useAppDispatch } from "hooks";
import { getRouteCompanyId } from "features/auth/access-lost";
import { fetchCabinetSummaryThunk, JUST_ASKED_MS } from "features/cabinet-tasks/cabinet-summary.slice";
import { ReviewQueueItem, SignQueueItem } from "services/cabinet-queues.service";
import {
  declineSigning,
  failureMessageKey,
  markAsReviewed,
  openDokobitSigning,
  signCurrentWithClick,
} from "./queue-item-requests";

/**
 * The flow of the «iš eilės» sheet: which document is open, what its buttons did, and
 * when the walk moves on. The sheet only draws what this hook says.
 *
 * The walk goes over a snapshot of documents taken when it opened, in one direction: a
 * document is acted on or skipped, and the walk never comes back to it, so a done document
 * is never shown again. The review walk asks for the rows after its last one when only a
 * few are left; it never asks for the first page again, which would bring skipped rows back.
 * While a request for the open document runs, the walk cannot be closed, so its answer is
 * always shown.
 */

/** A document the sheet walks over. */
export type WalkEntry = { kind: "sign"; item: SignQueueItem } | { kind: "review"; item: ReviewQueueItem };

/** What the sheet shows: team documents have a summary instead of a preview. */
export type SheetMode = "sign" | "team" | "review";

export interface WalkStart {
  entries: WalkEntry[];
  /** The N of «i iš N». */
  total: number;
  /** How many documents of the whole queue come before the first entry. */
  offset: number;
  /** Asks for the review rows after this cursor; left out when the walk is over these entries only. */
  loadRowsAfter?: (cursor: string) => Promise<ReviewQueueItem[]>;
}

/** Where the open document stands. Only `ready` and `failed` let the person act. */
export type Phase =
  | { name: "ready" }
  | { name: "working" }
  /** Done: the line is shown for a moment and the walk moves on by itself. */
  | { name: "moving-on"; messageKey: string }
  /** Dokobit was opened in another tab; the person moves on with «Toliau». */
  | { name: "dokobit-opened" }
  /** The request failed: the document stays and can be tried again or skipped. */
  | { name: "failed"; messageKey: string };

/** What the walk did so far, for the closing line. `done` is signed or reviewed. */
export interface Tally {
  done: number;
  declined: number;
  skipped: number;
  opened: number;
}

interface Session {
  /** Tells this walk from an earlier one, so an answer meant for a closed walk is not used by this one. */
  walkId: number;
  /** What the walk goes over; the closing line depends on it. */
  kind: WalkEntry["kind"];
  entries: WalkEntry[];
  index: number;
  total: number;
  offset: number;
  loadRowsAfter?: WalkStart["loadRowsAfter"];
  tally: Tally;
  phase: Phase;
  /** The last request for more rows came back empty. */
  noMoreRows: boolean;
  /** The last request for more rows failed; the walk ends with what it has. */
  moreRowsFailed: boolean;
}

/** What the sheet draws. */
export interface WalkView {
  kind: WalkEntry["kind"];
  mode: SheetMode;
  /** The open document; null while the next rows load and at the end. */
  entry: WalkEntry | null;
  /** The i of «i iš N». */
  position: number;
  total: number;
  phase: Phase;
  /** The buttons of the open document work: nothing is running and it is not waiting for «Toliau». */
  canAct: boolean;
  tally: Tally;
  isFinished: boolean;
  moreRowsFailed: boolean;
}

export interface QueueWalk {
  view: WalkView | null;
  open: (start: WalkStart) => void;
  /** Does nothing while a request for the open document runs. */
  close: () => void;
  /** The main button: sign, open Dokobit or mark as reviewed. */
  confirm: () => void;
  decline: (reason: string) => void;
  skip: () => void;
  /** After Dokobit was opened. */
  continueWalk: () => void;
}

const MOVE_ON_DELAY_MS = 800;
/** The review walk asks for more rows when fewer than this many are left. */
const ROWS_LEFT_BEFORE_ASKING = 3;

const canAct = (session: Session) => session.phase.name === "ready" || session.phase.name === "failed";

const moveOn = (session: Session): Session => ({ ...session, index: session.index + 1, phase: { name: "ready" } });

const withCount = (tally: Tally, key: keyof Tally): Tally => ({ ...tally, [key]: tally[key] + 1 });

/** Changes the session only while it is still the walk `walkId`, not a later one that replaced it. */
const forWalk = (walkId: number, change: (session: Session) => Session) => (current: Session | null) =>
  current && current.walkId === walkId ? change(current) : current;

function withMoreRows(session: Session, rows: ReviewQueueItem[]): Session {
  const knownIds = new Set(session.entries.map((entry) => entry.item.id));
  const fresh = rows.filter((row) => !knownIds.has(row.id)).map((item): WalkEntry => ({ kind: "review", item }));
  return { ...session, entries: [...session.entries, ...fresh], noMoreRows: fresh.length === 0 };
}

// With no document open (the end, or waiting for rows) the walk's own kind decides.
function modeOf(kind: WalkEntry["kind"], entry: WalkEntry | null): SheetMode {
  if (kind === "review") return "review";
  return entry?.kind === "sign" && entry.item.group === "team" ? "team" : "sign";
}

function viewOf(session: Session): WalkView {
  const entry = session.entries[session.index] ?? null;
  const canGetMoreRows = !!session.loadRowsAfter && !session.noMoreRows && !session.moreRowsFailed;
  return {
    kind: session.kind,
    mode: modeOf(session.kind, entry),
    entry,
    position: session.offset + session.index + 1,
    total: session.total,
    phase: session.phase,
    canAct: canAct(session),
    tally: session.tally,
    isFinished: !entry && !canGetMoreRows,
    moreRowsFailed: session.moreRowsFailed,
  };
}

/**
 * Runs the sheet for one queue page. `pageCompanyId` is the company in the address: the
 * summary is refreshed for it (the shared summary is that company's). `onClosed` lets the
 * page load its list again.
 */
export function useQueueWalk(pageCompanyId: string, onClosed: () => void): QueueWalk {
  const dispatch = useAppDispatch();
  const [session, setSession] = useState<Session | null>(null);
  // Moves on when a walk opens or closes and when the page goes away. A request that was
  // started before that belongs to a walk that is over.
  const walkCounter = useRef(0);
  const moveOnTimer = useRef<number>();
  // The cursor the last request for more rows was made for. The same cursor is never asked
  // twice; a new one comes with the rows that answered.
  const lastCursorAsked = useRef<string | null>(null);

  const refreshSummary = (maxAgeMs = 0) => dispatch(fetchCabinetSummaryThunk(pageCompanyId, { maxAgeMs }));

  const open = (start: WalkStart) => {
    walkCounter.current += 1;
    lastCursorAsked.current = null;
    window.clearTimeout(moveOnTimer.current);
    setSession({
      ...start,
      walkId: walkCounter.current,
      kind: start.entries[0].kind,
      index: 0,
      tally: { done: 0, declined: 0, skipped: 0, opened: 0 },
      phase: { name: "ready" },
      noMoreRows: false,
      moreRowsFailed: false,
    });
  };

  const close = () => {
    if (session?.phase.name === "working") return;
    walkCounter.current += 1;
    window.clearTimeout(moveOnTimer.current);
    setSession(null);
    onClosed();
    refreshSummary();
  };

  // Nothing is left running when the page goes away.
  useEffect(
    () => () => {
      walkCounter.current += 1;
      window.clearTimeout(moveOnTimer.current);
    },
    []
  );

  // The review walk tops its rows up before it runs dry. The answer only adds rows after the
  // last one the walk has, so it is used whenever it arrives.
  useEffect(() => {
    const loadRowsAfter = session?.loadRowsAfter;
    if (!session || !loadRowsAfter || session.noMoreRows || session.moreRowsFailed) return;
    if (session.entries.length - session.index >= ROWS_LEFT_BEFORE_ASKING) return;
    const lastEntry = session.entries[session.entries.length - 1];
    if (lastEntry?.kind !== "review" || lastEntry.item.cursor === lastCursorAsked.current) return;

    const { walkId } = session;
    lastCursorAsked.current = lastEntry.item.cursor;
    loadRowsAfter(lastEntry.item.cursor).then(
      (rows) => setSession(forWalk(walkId, (current) => withMoreRows(current, rows))),
      () => setSession(forWalk(walkId, (current) => ({ ...current, moreRowsFailed: true })))
    );
  }, [session]);

  /** Sends one request for the open document and, when it succeeds, moves on after a moment. */
  const perform = async (walkId: number, request: () => Promise<string>, countAs: keyof Tally) => {
    setSession(forWalk(walkId, (current) => ({ ...current, phase: { name: "working" } })));
    try {
      const messageKey = await request();
      if (walkCounter.current !== walkId) {
        // The page went away while the request ran, after it had asked the summary on leaving.
        // That answer did not include this one. (Not when the person is already in another
        // company: its own load asks for its summary.)
        if (getRouteCompanyId(window.location.pathname) === pageCompanyId) refreshSummary();
        return;
      }
      refreshSummary(JUST_ASKED_MS);
      setSession(
        forWalk(walkId, (current) => ({
          ...current,
          tally: withCount(current.tally, countAs),
          phase: { name: "moving-on", messageKey },
        }))
      );
      moveOnTimer.current = window.setTimeout(() => setSession(forWalk(walkId, moveOn)), MOVE_ON_DELAY_MS);
    } catch (error) {
      setSession(
        forWalk(walkId, (current) => ({ ...current, phase: { name: "failed", messageKey: failureMessageKey(error) } }))
      );
    }
  };

  const confirm = () => {
    const entry = session && canAct(session) ? session.entries[session.index] : null;
    if (!session || !entry) return;

    if (entry.kind === "review") {
      perform(session.walkId, () => markAsReviewed(entry.item), "done");
    } else if (entry.item.signType === "Dokobit") {
      // Opened straight from the click, which is what lets the new tab through the popup blocker.
      const wasOpened = openDokobitSigning(entry.item);
      setSession(
        forWalk(session.walkId, (current) =>
          wasOpened
            ? { ...current, tally: withCount(current.tally, "opened"), phase: { name: "dokobit-opened" } }
            : { ...current, phase: { name: "failed", messageKey: "queue_error_popup_blocked" } }
        )
      );
    } else {
      perform(session.walkId, () => signCurrentWithClick(entry.item), "done");
    }
  };

  const decline = (reason: string) => {
    const entry = session && canAct(session) ? session.entries[session.index] : null;
    if (!session || entry?.kind !== "sign") return;
    perform(session.walkId, () => declineSigning(entry.item, reason.trim()), "declined");
  };

  const skip = () => {
    if (!session || !canAct(session)) return;
    setSession(
      forWalk(session.walkId, (current) => moveOn({ ...current, tally: withCount(current.tally, "skipped") }))
    );
  };

  const continueWalk = () => {
    if (session?.phase.name !== "dokobit-opened") return;
    setSession(forWalk(session.walkId, moveOn));
  };

  return { view: session && viewOf(session), open, close, confirm, decline, skip, continueWalk };
}
